import { useCallback, useState } from "react";
import { CheckOutlined, DownloadOutlined, LinkOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { App, Dropdown } from "antd5";
import { ItemType } from "antd5/es/menu/interface";

import Button from "components/ui/button/Button";
import { copyToClipboard } from "lib/clipboard";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { BidReportEntry_Answer_Sources } from "lib/generated/app-service-gql/graphql";
import { useGetBidReportAnswers } from "lib/hooks/api/bid/answers/useGetBidReportAnswers";
import { BidReport, useBidReport } from "lib/hooks/api/bid/bid_reports/useBidReport";
import { useExportBidReportAsPDF } from "lib/hooks/api/bid/bid_reports/useExportBidReportAsPDF";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { routes } from "lib/routes";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { BidReportType } from "lib/types/graphQLEnums";
import { downloadFileFromLink, sanitizeFilename } from "lib/utils";
import { Flex, Text } from "../../../styles/utility-components";
import { DetailsContent } from "../../app_layout/DetailsLayout";
import { LoadingState } from "../../app_layout/LoadingState";
import { BidReportSection } from "../common_report_components/BidReportSection";
import { isNoticeSource } from "../common_report_components/DocumentSources";
import { DROPDOWN_CLOSE_DELAY_MS } from "../common_report_components/utils";
import {
  exportBidReportAsCsv,
  exportBidReportAsDocx,
} from "../requirement_summary_report_section/bidReportExportUtils";

import css from "./BidNoBidReport.module.scss";

type Props = {
  bidReportId: string;
};

/**
 * Stripped-back, read-only version of BidNoBidReport for public sharing.
 * Only renders: Q&A answer sections, notice/bid metadata, and export actions.
 * No edit controls, document slideout, regeneration, or "Ask AI" button.
 * No router dependency — this component is completely standalone.
 */
export function PublicBidNoBidReport({ bidReportId }: Props) {
  const { data: bidInfo, isLoading: isLoadingBidInfo } = useBidReport(bidReportId);
  const { data: answersData, isLoading: isLoadingAnswers } = useGetBidReportAnswers(bidReportId);

  const noticeId = bidInfo?.notice?.guid;
  const noticeTitle = bidInfo?.notice?.name ?? undefined;
  const buyerName = bidInfo?.notice?.buyers?.[0]?.name;
  const buyerId = bidInfo?.notice?.buyers?.[0]?.guid;

  const handleNoticeLink = useCallback(() => {
    if (noticeId) {
      window.open(`${routes.notices}/${noticeId}`, "_blank", "noopener,noreferrer");
    }
  }, [noticeId]);

  const onSelectDocument = useCallback(
    async (
      source: BidReportEntry_Answer_Sources,
      _trackingContext: EventNames,
      _trackingData: Record<string, unknown>,
    ) => {
      if (isNoticeSource(source, noticeId)) {
        handleNoticeLink();
      }
      // For document sources, no-op in the public view — there's no document slideout
    },
    [handleNoticeLink, noticeId],
  );

  if (isLoadingBidInfo || isLoadingAnswers) {
    return (
      <LoadingContainer>
        <LoadingState
          title="Loading bid / no-bid report"
          description="Please wait while we load the report."
        />
      </LoadingContainer>
    );
  }

  if (!bidInfo && (!answersData || answersData.length === 0)) {
    return (
      <LoadingContainer>
        <Text h2>Report not found</Text>
        <Text color="sysTextSecondary">
          This report may have been deleted or the link may be invalid.
        </Text>
      </LoadingContainer>
    );
  }

  return (
    <TrackingProvider data={{ "Report type": "Requirement summary" }}>
      <div className={css.qualificationReportPage}>
        <PublicReportActions
          bidReportId={bidReportId}
          bidName={bidInfo?.bid?.name}
          noticeId={noticeId}
          notice={bidInfo?.notice}
          answersData={answersData}
        />
        <DetailsContent className={css.pageContent}>
          <div className={css.answersWrapper}>
            <div className={css.noticeHeader}>
              <div className={css.titleContent}>
                <Text h1>Requirement summary</Text>
              </div>
              <div className={css.properties}>
                {noticeId && noticeTitle && (
                  <span className={css.property}>
                    <span className={css.propTitle}>
                      <UIcon className={css.icon} icon="file06" color={"sysTextSecondary"} />
                      Notice title
                    </span>
                    <EllipsisTooltipText
                      fullText={noticeTitle}
                      containerClassname={css.linkTitle}
                      textProps={{
                        onClick: handleNoticeLink,
                      }}
                    />
                  </span>
                )}
                {buyerName && buyerId && (
                  <span className={css.property}>
                    <span className={css.propTitle}>
                      <UIcon className={css.icon} color="sysTextSecondary" icon="wallet02" />
                      Buyer
                    </span>
                    <EllipsisTooltipText
                      fullText={buyerName}
                      containerClassname={css.linkTitle}
                      textProps={{
                        onClick: () =>
                          window.open(
                            `${routes.buyer}/${buyerId}`,
                            "_blank",
                            "noopener,noreferrer",
                          ),
                      }}
                    />
                  </span>
                )}
              </div>
            </div>
            <p className={css.explainerText}>
              Expand and review the information below to make your bid / no-bid decision in minutes,
              not days.
            </p>
            <div className={css.qaReportContent}>
              {answersData?.map((section) => (
                <BidReportSection
                  key={section.groupName}
                  answerGroup={section}
                  bidReportId={bidReportId}
                  reportType={BidReportType.RequirementSummary}
                  debugMode={false}
                  onSelectDocument={onSelectDocument}
                  slideoutOpen={false}
                  noticeId={noticeId}
                  noticeTitle={noticeTitle}
                  hasEditAccess={false}
                  isStreaming={false}
                  bidId={bidInfo?.bid?.id}
                  bidName={bidInfo?.bid?.name ?? ""}
                />
              ))}
            </div>
            <p className={css.explainerText}>
              *Stotles AI can make mistakes, important information should be verified.
            </p>
          </div>
        </DetailsContent>
      </div>
    </TrackingProvider>
  );
}

// --- Export actions header ---

type PublicReportActionsProps = {
  bidReportId: string;
  bidName?: string;
  noticeId?: string;
  notice?: BidReport["notice"];
  answersData: NonNullable<ReturnType<typeof useGetBidReportAnswers>["data"]> | undefined;
};

function PublicReportActions({
  bidReportId,
  bidName,
  noticeId,
  notice,
  answersData,
}: PublicReportActionsProps) {
  const { logEvent } = useTracking();
  const { message } = App.useApp();

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [isDocxExporting, setIsDocxExporting] = useState(false);

  const { mutate: exportBidReportAsPDF, isLoading: isPdfExporting } = useExportBidReportAsPDF(
    bidReportId,
    {
      onSuccess: (url) => {
        if (!url) {
          message.error("Failed to export bid report as PDF");
          return;
        }
        const fileName = `Bid_no_bid_report${"_" + sanitizeFilename(bidName || "")}.pdf`;
        downloadFileFromLink(url, fileName);
        logEvent(EventNames.exportedData, {
          "Record GUID": noticeId,
          "Bid report ID": bidReportId,
          "Action type": "Button clicked",
          "Data type": "Requirement summary (pdf)",
        });
      },
      onError: () => {
        message.error("Failed to export bid report as PDF");
      },
    },
  );

  const handleDocxExport = useCallback(async () => {
    if (!answersData) return;
    setIsDocxExporting(true);
    try {
      await exportBidReportAsDocx(answersData, bidName, notice);
      logEvent(EventNames.exportedData, {
        "Record GUID": noticeId,
        "Bid report ID": bidReportId,
        "Action type": "Button clicked",
        "Data type": "Requirement summary report (docx)",
      });
    } catch (e) {
      Sentry.captureException(e);
      message.error("Failed to export requirement summary report as Word document");
    } finally {
      setIsDocxExporting(false);
    }
  }, [answersData, bidName, notice, noticeId, bidReportId, logEvent, message]);

  const handleCSVExport = useCallback(() => {
    if (answersData) {
      exportBidReportAsCsv(answersData, bidName);
      logEvent(EventNames.exportedData, {
        "Record GUID": noticeId,
        "Bid report ID": bidReportId,
        "Action type": "Button clicked",
        "Data type": "Requirement summary (csv)",
      });
    }
  }, [answersData, logEvent, noticeId, bidName, bidReportId]);

  async function copyShareUrl() {
    const shareUrl = window.location.href;
    const clipboardContent = { plain: shareUrl };
    const copiedToClipboard = await copyToClipboard(clipboardContent);

    if (!copiedToClipboard) {
      void message.error("Failed to copy link to clipboard", 2);
      return;
    }

    logEvent(EventNames.qualReportLinkCopied, {
      "Action type": "Link clicked",
    });
    setIsLinkCopied(true);
  }

  const hasData = answersData && answersData.length > 0;

  const shareMenuItems: ItemType[] = [
    {
      key: "shareLink",
      label: isLinkCopied ? (
        <MenuItem primaryTextColor>
          <CheckOutlined />
          Link copied
        </MenuItem>
      ) : (
        <MenuItem onClick={async () => copyShareUrl()}>
          <LinkOutlined />
          Copy shareable link
        </MenuItem>
      ),
    },
    {
      key: "download-csv",
      label: (
        <MenuItem onClick={handleCSVExport}>
          <DownloadOutlined />
          Download CSV
        </MenuItem>
      ),
      disabled: !hasData,
    },
    {
      key: "download-docx",
      label: (
        <MenuItem onClick={handleDocxExport}>
          <UIcon icon={isDocxExporting ? "spinner" : "fileDownload02"} />
          Download Docx
        </MenuItem>
      ),
      disabled: isDocxExporting || !hasData,
    },
    {
      key: "download-pdf",
      label: (
        <MenuItem onClick={() => exportBidReportAsPDF()}>
          <UIcon icon={isPdfExporting ? "spinner" : "fileDownload02"} />
          Download PDF
        </MenuItem>
      ),
      disabled: isPdfExporting || !hasData,
    },
  ];

  return (
    <ActionBar>
      <ViewOnlyText>View-only</ViewOnlyText>
      <Dropdown
        menu={{ items: shareMenuItems }}
        overlayStyle={{ minWidth: "170px" }}
        open={isDropdownOpen}
        disabled={!hasData}
        mouseLeaveDelay={0.5}
        onOpenChange={(open) => {
          if (!open) {
            setTimeout(() => {
              setDropdownOpen(false);
              setIsLinkCopied(false);
            }, DROPDOWN_CLOSE_DELAY_MS);
          } else {
            setDropdownOpen(open);
          }
        }}
      >
        <Button buttonType="secondary" disabled={!hasData} icon="share07">
          <Text fontWeight="500">Share</Text>
          <UIcon icon="chevronDown" />
        </Button>
      </Dropdown>
    </ActionBar>
  );
}

const LoadingContainer = styled(Flex)({
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  gap: 16,
});

const MenuItem = styled(Flex)<{ primaryTextColor?: boolean }>(({ theme, primaryTextColor }) => ({
  alignItems: "center",
  gap: "8px",
  color: primaryTextColor ? theme.colors.sysPrimaryDefault : theme.colors.sysTextDefault,
}));

const ActionBar = styled(Flex)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 8,
  padding: "8px 24px",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const ViewOnlyText = styled(Text)(({ theme }) => ({
  color: theme.colors.sysPrimaryDefault,
  paddingRight: 24,
}));
